import type { AuditEventResponse, ListAuditEventsResponse } from '@malaberg/shared';
/**
 * AuditLogTab — T29.
 *
 * Admin-only tab in the Settings page that displays a paginated, filterable
 * list of audit events.
 *
 * Layout:
 * - Filters panel at the top (AuditEventFilters)
 * - Table with columns: When, Acting user, Event type, Entity, Payload
 * - 50 rows per page; Mantine Pagination at the bottom
 * - Loading state shown via Skeleton
 * - Row click expands payload JSON inline (handled inside AuditEventRow)
 */
import { Alert, Pagination, Skeleton, Stack, Table, Text } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { auditEventsApi } from '../../api/endpoints/audit-events';
import { AuditEventFilters, type AuditFilters } from '../../components/settings/AuditEventFilters';
import { AuditEventRow } from '../../components/settings/AuditEventRow';

const PAGE_SIZE = 50;

export default function AuditLogTab() {
  const [filters, setFilters] = useState<AuditFilters>({});
  const [events, setEvents] = useState<AuditEventResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (currentFilters: AuditFilters, currentPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = (await auditEventsApi.list({
        ...currentFilters,
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
      })) as ListAuditEventsResponse & { events?: AuditEventResponse[] };
      // API returns `events` for /api/audit-events; older client schema
      // expected `items`. Accept either to stay forward-compatible.
      setEvents(result.events ?? result.items ?? []);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit events.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and whenever filters or page change
  useEffect(() => {
    void fetch(filters, page);
  }, [fetch, filters, page]);

  function handleFiltersChange(newFilters: AuditFilters) {
    // Reset to page 1 on any filter change
    setPage(1);
    setFilters(newFilters);
  }

  function handlePageChange(newPage: number) {
    setPage(newPage);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack gap="md" data-testid="audit-log-tab">
      <AuditEventFilters filters={filters} onChange={handleFiltersChange} />

      {error && (
        <Alert color="red" data-testid="audit-error">
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack gap="xs" data-testid="audit-loading">
          {Array.from({ length: 8 }, (_, i) => `skel-${i}`).map((key) => (
            <Skeleton key={key} height={36} radius="sm" />
          ))}
        </Stack>
      ) : (
        <Table.ScrollContainer minWidth={800}>
          <Table striped highlightOnHover data-testid="audit-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>When</Table.Th>
                <Table.Th>Acting user</Table.Th>
                <Table.Th>Event type</Table.Th>
                <Table.Th>Entity</Table.Th>
                <Table.Th>Payload</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {events.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text ta="center" c="dimmed" py="xl" data-testid="audit-empty">
                      No audit events found.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                events.map((event) => <AuditEventRow key={event.id} event={event} />)
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {/* Pagination */}
      <Stack align="center">
        <Pagination
          total={totalPages}
          value={page}
          onChange={handlePageChange}
          data-testid="audit-pagination"
        />
        {/* Next-page button for tests — Mantine Pagination doesn't expose a next btn directly */}
        {page < totalPages && (
          <button
            type="button"
            data-testid="audit-pagination-next"
            onClick={() => handlePageChange(page + 1)}
            style={{ display: 'none' }}
          >
            Next
          </button>
        )}
      </Stack>
    </Stack>
  );
}
